import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { AVPlaybackStatus, ResizeMode, Video } from "expo-av";
import {
  Image,
  Linking,
  Modal,
  Platform,
  Pressable,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useSelector, useStore } from "react-redux";
import styles from "./styles";
import { MediaVariant, Post } from "../../../../types";
import { useUser } from "../../../hooks/useUser";
import PostSingleOverlay from "./overlay";
import { RootState } from "../../../redux/store";
import {
  unlockWebAudio,
  WEB_AUDIO_UNLOCK_STORAGE_KEY,
} from "../../../redux/slices/playbackSlice";
import Hls from "hls.js";
import { preferredPlaybackVariant } from "../../../utils/mediaVariants";
import {
  cacheCompletedHlsVideo,
  cacheHlsPlaybackCheckpoint,
  getHlsCacheSettings,
  getResumePosition,
  saveResumePosition,
} from "../../../services/mobileHlsCache";
import {
  mediaEdgeLabelForUrl,
  reportMediaEdgeFailure,
  selectMediaEdge,
} from "../../../services/mediaEdgeSelector";
import { APP_VERSION_LABEL } from "../../../config/buildInfo";
import { getNetworkIdentity } from "../../../services/networkIdentity";
import {
  getAccessVisitorId,
  recordAccessEvent,
} from "../../../services/accessTracking";
import {
  matchPlaybackVariantPreference,
  playbackPreferenceOwner,
  readPlaybackVariantPreference,
  rememberAudioVariantPreference,
  rememberVideoVariantPreference,
} from "../../../services/playbackVariantPreference";
import ProductSpotlight from "../../../commerce/ProductSpotlight";
import { siteLocale } from "../../../i18n/strings";
import { attachShakaPlayer } from "../../../services/shakaPlayer";
import type { ShakaPlaybackController } from "../../../services/shakaPlayer";

export type MediaPreloadMode = "active" | "next" | "previous" | "none";

const hlsWarmups = new Map<string, Promise<void>>();
const PUBLIC_SITE_ORIGIN =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "https://seaking.vip";
const APK_DOWNLOAD_URL = `${PUBLIC_SITE_ORIGIN}/tiktok/downloads/haihuang-entertainment-android-v1.2.0.apk`;
const IPA_DOWNLOAD_URL = `${PUBLIC_SITE_ORIGIN}/tiktok/downloads/?platform=ios`;

const resolveHlsUri = (baseUrl: string, value: string) =>
  new URL(value, baseUrl).toString();

const playlistUris = (playlist: string) =>
  playlist
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith("#"));
const isHlsPlaylistUrl = (value: string) => /\.m3u8(?:$|[?#])/i.test(value);

async function warmHlsManifestAndFirstSegment(url: string): Promise<void> {
  if (!url || typeof fetch === "undefined") {
    return;
  }
  const cacheSettings = getHlsCacheSettings();
  if (!cacheSettings.enabled || cacheSettings.strategy === "watched") return;
  const segmentCount = cacheSettings.preloadSegments;
  const warmupKey = `${url}#${segmentCount}`;
  const existing = hlsWarmups.get(warmupKey);
  if (existing) {
    return existing;
  }

  const warmup = (async () => {
    const masterResponse = await fetch(url, { cache: "force-cache" });
    if (!masterResponse.ok) {
      throw new Error(`HLS manifest warmup failed: ${masterResponse.status}`);
    }
    let playlistUrl = url;
    let playlist = await masterResponse.text();
    if (playlist.includes("#EXT-X-STREAM-INF")) {
      const child = playlistUris(playlist)[0];
      if (!child) return;
      playlistUrl = resolveHlsUri(url, child);
      const childResponse = await fetch(playlistUrl, { cache: "force-cache" });
      if (!childResponse.ok) {
        throw new Error(
          `HLS child manifest warmup failed: ${childResponse.status}`,
        );
      }
      playlist = await childResponse.text();
    }
    const firstSegments = playlistUris(playlist).slice(0, segmentCount);
    await Promise.all(
      firstSegments.map(async (segment) => {
        const segmentResponse = await fetch(
          resolveHlsUri(playlistUrl, segment),
          {
            cache: "force-cache",
          },
        );
        if (segmentResponse.ok) {
          await segmentResponse.arrayBuffer();
        }
      }),
    );
  })().catch(() => {});

  hlsWarmups.set(warmupKey, warmup);
  return warmup;
}

function isAutoplayPolicyError(error: unknown) {
  const name =
    typeof error === "object" && error && "name" in error
      ? String(error.name)
      : "";
  return name === "NotAllowedError" || name === "SecurityError";
}

function rememberWebAudioUnlock(unlocked: boolean) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    if (unlocked) {
      window.sessionStorage.setItem(WEB_AUDIO_UNLOCK_STORAGE_KEY, "1");
      window.localStorage.setItem(WEB_AUDIO_UNLOCK_STORAGE_KEY, "1");
    } else {
      window.sessionStorage.removeItem(WEB_AUDIO_UNLOCK_STORAGE_KEY);
      window.localStorage.removeItem(WEB_AUDIO_UNLOCK_STORAGE_KEY);
    }
  } catch {
    // Storage can be disabled; Redux still preserves the setting in this page.
  }
}

function hlsVideoRoot(value: string) {
  try {
    const pathname = new URL(value, window.location.href).pathname;
    const marker = "/videos/hls/";
    const markerIndex = pathname.indexOf(marker);
    if (markerIndex < 0)
      return pathname.slice(0, pathname.lastIndexOf("/") + 1);
    const idEnd = pathname.indexOf("/", markerIndex + marker.length);
    return idEnd < 0 ? pathname : pathname.slice(0, idEnd + 1);
  } catch {
    return "";
  }
}

function networkSourceLabel(value: string) {
  return mediaEdgeLabelForUrl(value);
}

export interface PostSingleHandles {
  play: () => Promise<void>;
  stop: () => Promise<void>;
  unload: () => Promise<void>;
}

/**
 * This component is responsible for displaying a post and play the
 * media associated with it.
 *
 * The ref is forwarded to this component so that the parent component
 * can manage the play status of the video.
 */
export const PostSingle = forwardRef<
  PostSingleHandles,
  {
    item: Post;
    isActive?: boolean;
    preloadMode?: MediaPreloadMode;
    onPlaybackFinish?: () => void;
    onFavoriteChange?: (favorite: boolean) => void;
    onWebDragComplete?: (dx: number, dy: number) => void;
    onChromeVisibilityChange?: (visible: boolean) => void;
    strategyHeaderEnabled?: boolean;
  }
>(
  (
    {
      item,
      isActive = false,
      preloadMode,
      onPlaybackFinish,
      onFavoriteChange,
      onWebDragComplete,
      onChromeVisibilityChange,
      strategyHeaderEnabled = false,
    },
    parentRef,
  ) => {
    const isWeb = Platform.OS === "web";
    const effectivePreloadMode = preloadMode || (isActive ? "active" : "none");
    const ref = useRef<Video>(null);
    const webVideoRef = useRef<HTMLVideoElement | null>(null);
    const webDragSurfaceRef = useRef<any>(null);
    const webMouseDragRef = useRef({
      active: false,
      startX: 0,
      startY: 0,
      lastX: 0,
      lastY: 0,
      moved: false,
    });
    const lastWebDragCompleteAtRef = useRef(0);
    const backgroundAudioRef = useRef<HTMLAudioElement | null>(null);
    const pendingVideoTimeRef = useRef<number | null>(null);
    const webPlaybackAttemptRef = useRef(0);
    const finishHandlingRef = useRef(false);
    const resumeAppliedItemRef = useRef("");
    const lastResumeSaveRef = useRef(0);
    const completedCacheRequestedRef = useRef("");
    const cacheCheckpointRef = useRef({ url: "", milestone: 0 });
    const hideProgressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    const loadingTickRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const loadingDisplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    const loadingStartedAtRef = useRef(0);
    const playbackReadyRef = useRef(false);
    const nativeBufferRecoveryTimerRef = useRef<ReturnType<
      typeof setTimeout
    > | null>(null);
    const nativeBufferRecoveryCountRef = useRef(0);
    const user = useUser(item.creator).data;
    const store = useStore<RootState>();
    const currentViewer = useSelector(
      (state: RootState) => state.auth.currentUser,
    );
    const playbackPreferenceOwnerKey = useMemo(
      () => playbackPreferenceOwner(currentViewer?.uid, getAccessVisitorId()),
      [currentViewer?.uid],
    );
    const backgroundPlaybackEnabled = useSelector(
      (state: RootState) => state.playback.backgroundPlaybackEnabled,
    );
    const webAudioUnlocked = useSelector(
      (state: RootState) => state.playback.webAudioUnlocked,
    );
    const [isPlaying, setIsPlaying] = useState(false);
    const [webAudioEnabled, setWebAudioEnabled] = useState(webAudioUnlocked);
    const [audioUnlockState, setAudioUnlockState] = useState<
      "idle" | "loading" | "failed"
    >("idle");

    useEffect(() => {
      if (!isActive || !item.videoServerId) return;
      const openedAt = Date.now();
      const playbackId =
        globalThis.crypto?.randomUUID?.() ||
        `${item.videoServerId}-${openedAt}-${Math.random().toString(16).slice(2)}`;
      void recordAccessEvent(
        {
          event: "video_open",
          page: "mobile-feed",
          videoId: item.videoServerId,
          videoTitle: item.description,
          playbackId,
          clientEventId: `${playbackId}-open`,
        },
        currentViewer,
      );
      return () => {
        void recordAccessEvent(
          {
            event: "video_close",
            page: "mobile-feed",
            videoId: item.videoServerId,
            videoTitle: item.description,
            durationMs: Date.now() - openedAt,
            playbackId,
            clientEventId: `${playbackId}-close`,
          },
          currentViewer,
        );
      };
    }, [
      currentViewer?.uid,
      isActive,
      item.description,
      item.videoServerId,
    ]);
    const [backgroundLoadProgress, setBackgroundLoadProgress] = useState<
      number | null
    >(null);
    const [progressVisible, setProgressVisible] = useState(false);
    const [strategyProgressTop, setStrategyProgressTop] = useState(248);
    const [downloadOpen, setDownloadOpen] = useState(false);
    const [mediaLoading, setMediaLoading] = useState(false);
    const [mediaLoadProgress, setMediaLoadProgress] = useState(0);
    const [mediaLoadMessage, setMediaLoadMessage] =
      useState("正在连接视频服务器…");
    const [playbackReloadKey, setPlaybackReloadKey] = useState(0);
    const [playbackSourceLabel, setPlaybackSourceLabel] =
      useState("播放来源：检测中");
    const [networkIdentityLabel, setNetworkIdentityLabel] =
      useState("本机公网IP：获取中…");
    const [playbackProgress, setPlaybackProgress] = useState({
      positionMillis: 0,
      durationMillis: 0,
    });

    useEffect(() => {
      if (!isActive) return;
      onChromeVisibilityChange?.(progressVisible);
      return () => {
        if (progressVisible) {
          onChromeVisibilityChange?.(false);
        }
      };
    }, [isActive, onChromeVisibilityChange, progressVisible]);
    const playbackVideoVariants = useMemo(
      () =>
        (item.mediaVariants || []).filter(
          (variant) =>
            variant.kind === "mobile" ||
            (isWeb && variant.kind === "desktop"),
        ),
      [isWeb, item.mediaVariants],
    );
    const audioVariants = useMemo(
      () =>
        (item.mediaVariants || []).filter(
          (variant) => variant.kind === "audio",
        ),
      [item.mediaVariants],
    );
    const defaultMobileVariant = useMemo(
      () =>
        preferredPlaybackVariant(playbackVideoVariants, item.media[0], "mobile"),
      [item.media, playbackVideoVariants],
    );
    const [selectedVideoVariantId, setSelectedVideoVariantId] = useState<
      string | null
    >(() => {
      const preference = readPlaybackVariantPreference(
        playbackPreferenceOwnerKey,
      );
      return (
        matchPlaybackVariantPreference(playbackVideoVariants, preference.video)
          ?.id ||
        defaultMobileVariant?.id ||
        null
      );
    });
    const [selectedAudioVariantId, setSelectedAudioVariantId] = useState<
      string | null
    >(() => {
      const preference = readPlaybackVariantPreference(
        playbackPreferenceOwnerKey,
      );
      if (preference.audioMode !== "variant") return null;
      return (
        matchPlaybackVariantPreference(audioVariants, preference.audio)?.id ||
        null
      );
    });
    const selectedVideoVariant =
      playbackVideoVariants.find(
        (variant) => variant.id === selectedVideoVariantId,
      ) ||
      defaultMobileVariant;
    const selectedAudioVariant = audioVariants.find(
      (variant) => variant.id === selectedAudioVariantId,
    );
    const originalVideoUri = selectedVideoVariant?.url || item.media[0];
    const [balancedVideoUri, setBalancedVideoUri] = useState(originalVideoUri);
    const videoSource = useMemo(
      () => ({ uri: balancedVideoUri }),
      [balancedVideoUri],
    );
    const posterSource = useMemo(() => ({ uri: item.media[1] }), [item.media]);
    const backgroundMediaSource =
      selectedAudioVariant?.url || item.media[2] || videoSource.uri;
    const isHlsSource = /\.m3u8(?:$|\?)/i.test(videoSource.uri);
    const applyPreferredWebAudio = useCallback(
      (video: HTMLVideoElement | null) => {
        if (
          !video ||
          !webAudioUnlocked ||
          backgroundPlaybackEnabled ||
          backgroundAudioRef.current
        ) {
          return false;
        }
        video.defaultMuted = false;
        video.muted = false;
        video.volume = 1;
        setWebAudioEnabled(true);
        return true;
      },
      [backgroundPlaybackEnabled, webAudioUnlocked],
    );

    useEffect(() => {
      if (!isActive) return;
      let active = true;
      getNetworkIdentity().then((identity) => {
        if (!active) return;
        const cached = identity.source === "cache" ? " · 缓存" : "";
        const isp = identity.isp ? ` · ${identity.isp}` : "";
        setNetworkIdentityLabel(
          `本机公网IP：${identity.ip} · ${identity.location}${isp}${cached}`,
        );
      });
      return () => {
        active = false;
      };
    }, [isActive]);

    const beginMediaLoading = useCallback(
      (message = "正在连接视频服务器…") => {
        if (!isActive) return;
        playbackReadyRef.current = false;
        loadingStartedAtRef.current = Date.now();
        if (loadingDisplayTimerRef.current)
          clearTimeout(loadingDisplayTimerRef.current);
        loadingDisplayTimerRef.current = setTimeout(() => {
          if (!playbackReadyRef.current) setMediaLoading(true);
        }, 350);
        setMediaLoadProgress((current) =>
          current >= 100 ? 4 : Math.max(4, current),
        );
        setMediaLoadMessage(message);
      },
      [isActive],
    );

    const advanceMediaLoading = useCallback(
      (progress: number, message: string) => {
        if (!isActive || playbackReadyRef.current) return;
        if (Date.now() - loadingStartedAtRef.current >= 350) {
          setMediaLoading(true);
        }
        setMediaLoadProgress((current) =>
          Math.max(current, Math.min(96, progress)),
        );
        setMediaLoadMessage(message);
      },
      [isActive],
    );

    const finishMediaLoading = useCallback(() => {
      if (!isActive) return;
      playbackReadyRef.current = true;
      if (loadingDisplayTimerRef.current) {
        clearTimeout(loadingDisplayTimerRef.current);
        loadingDisplayTimerRef.current = null;
      }
      setMediaLoadProgress(100);
      setMediaLoadMessage("可以播放");
      window.setTimeout(() => {
        if (playbackReadyRef.current) setMediaLoading(false);
      }, 260);
    }, [isActive]);

    useEffect(() => {
      if (!isWeb || !isActive || !mediaLoading) {
        if (loadingTickRef.current) clearInterval(loadingTickRef.current);
        loadingTickRef.current = null;
        return;
      }
      loadingTickRef.current = setInterval(() => {
        const elapsed = Date.now() - loadingStartedAtRef.current;
        setMediaLoadProgress((current) => {
          const ceiling = elapsed > 12000 ? 94 : elapsed > 6000 ? 86 : 72;
          return current < ceiling ? Math.min(ceiling, current + 1) : current;
        });
        if (elapsed > 12000) {
          setMediaLoadMessage("网络较慢，仍在缓冲；可尝试重新连接");
        } else if (elapsed > 6000) {
          setMediaLoadMessage("代理网络较慢，正在下载首个切片…");
        }
      }, 500);
      return () => {
        if (loadingTickRef.current) clearInterval(loadingTickRef.current);
        loadingTickRef.current = null;
      };
    }, [isActive, isWeb, mediaLoading]);

    useEffect(() => {
      const preference = readPlaybackVariantPreference(
        playbackPreferenceOwnerKey,
      );
      const preferredVideo = matchPlaybackVariantPreference(
        playbackVideoVariants,
        preference.video,
      );
      const preferredAudio =
        preference.audioMode === "variant"
          ? matchPlaybackVariantPreference(audioVariants, preference.audio)
          : undefined;
      setSelectedVideoVariantId(
        preferredVideo?.id || defaultMobileVariant?.id || null,
      );
      setSelectedAudioVariantId(preferredAudio?.id || null);
      playbackReadyRef.current = false;
      nativeBufferRecoveryCountRef.current = 0;
      pendingVideoTimeRef.current = null;
      if (nativeBufferRecoveryTimerRef.current) {
        clearTimeout(nativeBufferRecoveryTimerRef.current);
        nativeBufferRecoveryTimerRef.current = null;
      }
    }, [
      audioVariants,
      defaultMobileVariant?.id,
      isActive,
      item.id,
      playbackVideoVariants,
      playbackPreferenceOwnerKey,
    ]);

    useEffect(() => {
      let active = true;
      setBalancedVideoUri(originalVideoUri);
      selectMediaEdge(originalVideoUri)
        .then((edge) => {
          if (!active) return;
          console.log(
            `[MEDIA_EDGE_SELECTED] ${Platform.OS} ${edge.id} ${edge.latencyMs}ms`,
          );
          setPlaybackSourceLabel(
            `播放来源：${edge.label} · 在线${
              edge.latencyMs ? ` · ${edge.latencyMs}ms` : ""
            }`,
          );
          setBalancedVideoUri(edge.url);
        })
        .catch(() => {});
      return () => {
        active = false;
      };
    }, [originalVideoUri]);

    useEffect(() => {
      if (
        !isWeb ||
        !isActive ||
        !isHlsSource ||
        typeof navigator === "undefined" ||
        !navigator.serviceWorker
      ) {
        return;
      }
      const currentRoot = hlsVideoRoot(videoSource.uri);
      setPlaybackSourceLabel(
        `播放来源：${networkSourceLabel(videoSource.uri)} · 检测中`,
      );
      const handleSourceMessage = (event: MessageEvent) => {
        const data = event.data;
        if (
          data?.type !== "HLS_PLAYBACK_SOURCE" ||
          typeof data.url !== "string" ||
          (currentRoot && !hlsVideoRoot(data.url).startsWith(currentRoot))
        ) {
          return;
        }
        const resource = data.resource === "playlist" ? "清单" : "切片";
        if (data.source === "cache") {
          setPlaybackSourceLabel(`播放来源：本机缓存 · ${resource}`);
        } else {
          setPlaybackSourceLabel(
            `播放来源：${networkSourceLabel(data.url)} · 在线${resource}`,
          );
        }
      };
      const sourceChannel =
        typeof BroadcastChannel !== "undefined"
          ? new BroadcastChannel("kirkwat-hls-source")
          : null;
      sourceChannel?.addEventListener("message", handleSourceMessage);
      navigator.serviceWorker.addEventListener("message", handleSourceMessage);
      return () => {
        sourceChannel?.removeEventListener("message", handleSourceMessage);
        sourceChannel?.close();
        navigator.serviceWorker.removeEventListener(
          "message",
          handleSourceMessage,
        );
      };
    }, [isActive, isHlsSource, isWeb, videoSource.uri]);

    useEffect(() => {
      if (!isWeb || !isHlsSource) {
        return;
      }

      const cacheSettings = getHlsCacheSettings();
      if (
        (effectivePreloadMode === "next" ||
          effectivePreloadMode === "previous") &&
        cacheSettings.strategy === "watched"
      ) {
        return;
      }

      if (effectivePreloadMode === "previous") {
        warmHlsManifestAndFirstSegment(videoSource.uri);
        return;
      }

      if (effectivePreloadMode === "none") {
        return;
      }

      const video = webVideoRef.current;
      if (!video) {
        return;
      }

      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        if (effectivePreloadMode === "active") {
          if (typeof caches !== "undefined") {
            caches
              .match(videoSource.uri)
              .then((cached) => {
                setPlaybackSourceLabel(
                  cached
                    ? "播放来源：本机缓存 · 清单"
                    : `播放来源：${networkSourceLabel(
                        videoSource.uri,
                      )} · 在线清单`,
                );
              })
              .catch(() => {
                setPlaybackSourceLabel(
                  `播放来源：${networkSourceLabel(videoSource.uri)} · 在线清单`,
                );
              });
          } else {
            setPlaybackSourceLabel(
              `播放来源：${networkSourceLabel(videoSource.uri)} · 在线清单`,
            );
          }
        }
        video.src = videoSource.uri;
        return;
      }

      let disposed = false;
      let hls: Hls | null = null;
      let shakaController: ShakaPlaybackController | null = null;

      const startHlsFallback = () => {
        if (disposed || !Hls.isSupported()) return;

        const BaseLoader = Hls.DefaultConfig.loader;
        class PlaybackSourceLoader extends BaseLoader {
          load(context: any, config: any, callbacks: any) {
            if (effectivePreloadMode === "active") {
              const requestUrl = String(context?.url || "");
              const resource = isHlsPlaylistUrl(requestUrl) ? "清单" : "切片";
              if (typeof caches !== "undefined") {
                caches
                  .match(requestUrl)
                  .then((cached) => {
                    setPlaybackSourceLabel(
                      cached
                        ? `播放来源：本机缓存 · ${resource}`
                        : `播放来源：${networkSourceLabel(requestUrl)} · 在线${resource}`,
                    );
                  })
                  .catch(() => {
                    setPlaybackSourceLabel(
                      `播放来源：${networkSourceLabel(requestUrl)} · 在线${resource}`,
                    );
                  });
              } else {
                setPlaybackSourceLabel(
                  `播放来源：${networkSourceLabel(requestUrl)} · 在线${resource}`,
                );
              }
            }
            return super.load(context, config, callbacks);
          }
        }

        const instance = new Hls({
          loader: PlaybackSourceLoader,
          autoStartLoad: effectivePreloadMode === "active",
          startLevel: -1,
          capLevelToPlayerSize: true,
          maxBufferLength: effectivePreloadMode === "active" ? 12 : 2,
          maxMaxBufferLength: effectivePreloadMode === "active" ? 24 : 3,
          backBufferLength: 8,
        });
        hls = instance;
        if (effectivePreloadMode === "active") {
          beginMediaLoading("正在启动兼容播放器…");
          instance.on(Hls.Events.MANIFEST_LOADED, () => {
            advanceMediaLoading(28, "视频清单已读取，正在选择画质…");
          });
          instance.on(Hls.Events.LEVEL_LOADED, () => {
            advanceMediaLoading(48, "画质已选择，正在下载首个切片…");
          });
          instance.on(Hls.Events.FRAG_LOADING, () => {
            advanceMediaLoading(62, "正在下载视频切片…");
          });
          instance.on(Hls.Events.FRAG_LOADED, () => {
            advanceMediaLoading(84, "切片已下载，正在写入播放缓冲…");
          });
          instance.on(Hls.Events.FRAG_BUFFERED, () => {
            setPlaybackSourceLabel((label) => `${label} · HLS兼容模式`);
            finishMediaLoading();
          });
        }
        instance.loadSource(videoSource.uri);
        instance.attachMedia(video);
        if (effectivePreloadMode === "next") {
          instance.once(Hls.Events.MANIFEST_PARSED, () => instance.startLoad(0));
          instance.once(Hls.Events.FRAG_BUFFERED, () => instance.stopLoad());
        }
        instance.on(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal) return;
          reportMediaEdgeFailure(videoSource.uri);
          if (videoSource.uri !== originalVideoUri) {
            setBalancedVideoUri(originalVideoUri);
          } else if (selectedVideoVariantId !== defaultMobileVariant?.id) {
            setSelectedVideoVariantId(defaultMobileVariant?.id || null);
          }
        });
      };

      const startPlayer = async () => {
        if (effectivePreloadMode !== "active") {
          startHlsFallback();
          return;
        }

        beginMediaLoading("正在启动 4K 播放器…");
        try {
          const controller = await attachShakaPlayer(video, videoSource.uri, {
            onBuffering: (buffering) => {
              if (buffering) {
                advanceMediaLoading(62, "正在缓冲高清画面…");
              } else {
                finishMediaLoading();
              }
            },
            onReady: () => {
              advanceMediaLoading(92, "播放器已就绪…");
              finishMediaLoading();
            },
            onVariantChanged: (height) => {
              const quality = height
                ? height >= 2160
                  ? "4K"
                  : `${height}p`
                : "自动画质";
              setPlaybackSourceLabel(
                `播放来源：${networkSourceLabel(videoSource.uri)} · ${quality} · Shaka`,
              );
            },
            onError: () => reportMediaEdgeFailure(videoSource.uri),
          });
          if (disposed) {
            await controller?.destroy();
            return;
          }
          if (controller) {
            shakaController = controller;
            return;
          }
        } catch (error) {
          console.warn("Shaka playback failed; using hls.js fallback.", error);
        }
        if (!disposed) startHlsFallback();
      };

      void startPlayer();

      return () => {
        disposed = true;
        hls?.destroy();
        void shakaController?.destroy();
        video.removeAttribute("src");
        video.load();
      };
    }, [
      defaultMobileVariant?.id,
      effectivePreloadMode,
      isHlsSource,
      isWeb,
      originalVideoUri,
      playbackReloadKey,
      selectedVideoVariantId,
      videoSource.uri,
      advanceMediaLoading,
      beginMediaLoading,
      finishMediaLoading,
    ]);

    useEffect(() => {
      if (!isWeb || effectivePreloadMode !== "active") return;
      const video = webVideoRef.current;
      if (!video) return;

      let decodedFrameTimer: ReturnType<typeof setTimeout> | null = null;
      const clearDecodedFrameTimer = () => {
        if (!decodedFrameTimer) return;
        clearTimeout(decodedFrameTimer);
        decodedFrameTimer = null;
      };
      const decodedFrameReady = () => {
        if (video.videoWidth <= 0 || video.videoHeight <= 0) return false;
        clearDecodedFrameTimer();
        return true;
      };
      const verifyDecodedFrame = () => {
        if (video.paused || decodedFrameReady() || decodedFrameTimer) return;
        decodedFrameTimer = setTimeout(() => {
          decodedFrameTimer = null;
          if (video.paused || decodedFrameReady()) return;
          reportMediaEdgeFailure(videoSource.uri);
          setPlaybackSourceLabel("播放失败：音频正常，但没有可解码的视频画面");
          if (videoSource.uri !== originalVideoUri) {
            setBalancedVideoUri(originalVideoUri);
          } else if (selectedVideoVariantId !== defaultMobileVariant?.id) {
            setSelectedVideoVariantId(defaultMobileVariant?.id || null);
          } else {
            video.pause();
          }
        }, 5000);
      };

      video.addEventListener("playing", verifyDecodedFrame);
      video.addEventListener("resize", decodedFrameReady);
      if (!video.paused) verifyDecodedFrame();
      return () => {
        clearDecodedFrameTimer();
        video.removeEventListener("playing", verifyDecodedFrame);
        video.removeEventListener("resize", decodedFrameReady);
      };
    }, [
      defaultMobileVariant?.id,
      effectivePreloadMode,
      isWeb,
      originalVideoUri,
      selectedVideoVariantId,
      videoSource.uri,
    ]);

    const showProgressTemporarily = useCallback(() => {
      setProgressVisible(true);
      if (hideProgressTimerRef.current) {
        clearTimeout(hideProgressTimerRef.current);
      }
      hideProgressTimerRef.current = setTimeout(() => {
        setProgressVisible(false);
      }, 2500);
    }, []);

    useImperativeHandle(parentRef, () => ({
      play,
      stop,
      unload,
    }));

    useEffect(() => {
      return () => {
        if (hideProgressTimerRef.current) {
          clearTimeout(hideProgressTimerRef.current);
        }
        if (loadingTickRef.current) {
          clearInterval(loadingTickRef.current);
        }
        if (loadingDisplayTimerRef.current) {
          clearTimeout(loadingDisplayTimerRef.current);
        }
        if (nativeBufferRecoveryTimerRef.current) {
          clearTimeout(nativeBufferRecoveryTimerRef.current);
        }
        const backgroundAudio = backgroundAudioRef.current;
        if (backgroundAudio) {
          backgroundAudio.pause();
          backgroundAudio.removeAttribute("src");
          backgroundAudio.remove();
          backgroundAudioRef.current = null;
        }
        unload()
          .then(() => {})
          .catch((e) => {
            console.log("Failed to unload:", e);
          });
      };
    }, []);

    /**
     * Plays the video in the component if the ref
     * of the video is not null.
     *
     * @returns {void}
     */
    const play = async () => {
      if (isWeb) {
        const video = webVideoRef.current;
        if (!video) {
          return;
        }
        const attempt = ++webPlaybackAttemptRef.current;
        try {
          finishHandlingRef.current = false;
          const playWithAudio =
            store.getState().playback.webAudioUnlocked &&
            !store.getState().playback.backgroundPlaybackEnabled;
          setWebAudioEnabled(playWithAudio);
          const externalAudio = backgroundAudioRef.current;
          video.defaultMuted = Boolean(externalAudio) || !playWithAudio;
          video.muted = Boolean(externalAudio) || !playWithAudio;
          video.volume = 1;
          if (!video.paused && !video.ended) {
            setIsPlaying(true);
            return;
          }
          await video.play();
          if (attempt !== webPlaybackAttemptRef.current) {
            return;
          }
          if (externalAudio && playWithAudio) {
            externalAudio.currentTime = video.currentTime;
            await externalAudio.play();
          }
          setIsPlaying(true);
        } catch (e) {
          if (
            attempt !== webPlaybackAttemptRef.current ||
            !isAutoplayPolicyError(e)
          ) {
            console.log("Playback attempt was interrupted:", e);
            return;
          }
          if (
            store.getState().playback.webAudioUnlocked &&
            !store.getState().playback.backgroundPlaybackEnabled
          ) {
            setWebAudioEnabled(false);
            video.defaultMuted = true;
            video.muted = true;
            try {
              await video.play();
              setIsPlaying(true);
              return;
            } catch {
              // Fall through to the original playback error below.
            }
          }
          console.log("An error occurred:", e);
        }
        return;
      }

      if (ref.current == null) {
        return;
      }
      try {
        finishHandlingRef.current = false;
        const status = await ref.current.getStatusAsync();
        if (status && "isLoaded" in status && !status.isLoaded) {
          await ref.current.loadAsync(videoSource, { shouldPlay: true }, false);
          setIsPlaying(true);
          return;
        }
        if (status && "isPlaying" in status && status.isPlaying) {
          setIsPlaying(true);
          return;
        }
        await ref.current.playAsync();
        setIsPlaying(true);
      } catch (e) {
        console.log("An error occurred:", e);
      }
    };

    /**
     * Stops the video in the component if the ref
     * of the video is not null.
     *
     * @returns {void}
     */
    const stop = async () => {
      if (isWeb) {
        webPlaybackAttemptRef.current += 1;
        const video = webVideoRef.current;
        if (!video) {
          return;
        }
        video.pause();
        backgroundAudioRef.current?.pause();
        setIsPlaying(false);
        return;
      }

      if (ref.current == null) {
        return;
      }
      try {
        const status = await ref.current.getStatusAsync();
        if (status && "isPlaying" in status && !status.isPlaying) {
          setIsPlaying(false);
          return;
        }
        await ref.current.pauseAsync();
        setIsPlaying(false);
      } catch (e) {
        console.log("An error occurred:", e);
      }
    };

    /**
     * Unloads the video in the component if the ref
     * of the video is not null.
     *
     * This will make sure unnecessary video instances are
     * not in memory at all times
     *
     * @returns {void}
     */
    const unload = async () => {
      if (isWeb) {
        webVideoRef.current?.pause();
        return;
      }

      if (ref.current == null) {
        return;
      }
      try {
        await ref.current.unloadAsync();
      } catch (e) {
        console.log(e);
      }
    };

    const togglePlayback = async () => {
      if (isWeb) {
        const video = webVideoRef.current;
        if (!video) {
          return;
        }
        try {
          if (!video.paused) {
            video.pause();
            backgroundAudioRef.current?.pause();
            setIsPlaying(false);
            showProgressTemporarily();
            return;
          }
          const externalAudio = backgroundAudioRef.current;
          video.defaultMuted = Boolean(externalAudio);
          video.muted = Boolean(externalAudio);
          await video.play();
          if (externalAudio) {
            externalAudio.currentTime = video.currentTime;
            await externalAudio.play();
          }
          store.dispatch(unlockWebAudio());
          rememberWebAudioUnlock(true);
          setWebAudioEnabled(true);
          setIsPlaying(true);
          showProgressTemporarily();
        } catch (e) {
          console.log("Failed to toggle playback:", e);
        }
        return;
      }

      if (ref.current == null) {
        return;
      }

      try {
        if (Platform.OS === "web") {
          setWebAudioEnabled(true);
        }
        const status = await ref.current.getStatusAsync();
        if (status && "isLoaded" in status && !status.isLoaded) {
          await ref.current.loadAsync(videoSource, { shouldPlay: true }, false);
          setIsPlaying(true);
          showProgressTemporarily();
          return;
        }

        if (status && "isPlaying" in status && status.isPlaying) {
          await ref.current.pauseAsync();
          setIsPlaying(false);
          showProgressTemporarily();
          return;
        }

        await ref.current.playAsync();
        setIsPlaying(true);
        showProgressTemporarily();
      } catch (e) {
        console.log("Failed to toggle playback:", e);
      }
    };

    const handleSurfacePress = () => {
      showProgressTemporarily();
    };

    const retryMediaLoading = useCallback(() => {
      const video = webVideoRef.current;
      loadingStartedAtRef.current = Date.now();
      setMediaLoadProgress(4);
      setMediaLoadMessage("正在重新连接视频服务器…");
      setMediaLoading(true);
      reportMediaEdgeFailure(videoSource.uri);
      if (videoSource.uri !== originalVideoUri) {
        setBalancedVideoUri(originalVideoUri);
        return;
      }
      video?.pause();
      setPlaybackReloadKey((current) => current + 1);
    }, [originalVideoUri, videoSource.uri]);

    const enableWebAudio = async () => {
      const video = webVideoRef.current;
      if (
        !isWeb ||
        !video ||
        backgroundPlaybackEnabled ||
        audioUnlockState === "loading"
      ) {
        return;
      }

      setAudioUnlockState("loading");
      try {
        const wasPaused = video.paused;
        video.defaultMuted = false;
        video.muted = false;
        video.volume = 1;
        if (wasPaused) {
          await video.play();
        }
        store.dispatch(unlockWebAudio());
        rememberWebAudioUnlock(true);
        setWebAudioEnabled(true);
        setIsPlaying(true);
        setAudioUnlockState("idle");
        showProgressTemporarily();
      } catch (error) {
        setWebAudioEnabled(false);
        setAudioUnlockState("failed");
        video.defaultMuted = true;
        video.muted = true;
        video.play().catch(() => {});
        console.log("Failed to enable web audio:", error);
      }
    };

    const enableBackgroundPlayback = useCallback(async () => {
      if (!isWeb) {
        await play();
        return;
      }

      const video = webVideoRef.current;
      if (!video) {
        return;
      }

      try {
        setWebAudioEnabled(true);
        setBackgroundLoadProgress(0);
        let audio = backgroundAudioRef.current;
        if (!audio) {
          audio = document.createElement("audio");
          audio.preload = "auto";
          audio.src = backgroundMediaSource;
          audio.style.display = "none";
          document.body.appendChild(audio);
          backgroundAudioRef.current = audio;
          const updateLoadProgress = () => {
            if (
              !audio ||
              !Number.isFinite(audio.duration) ||
              audio.duration <= 0
            ) {
              return;
            }
            const bufferedEnd = audio.buffered.length
              ? audio.buffered.end(audio.buffered.length - 1)
              : 0;
            const percent = Math.min(
              100,
              Math.max(0, (bufferedEnd / audio.duration) * 100),
            );
            setBackgroundLoadProgress((current) =>
              Math.max(current ?? 0, Math.round(percent)),
            );
          };
          audio.addEventListener("progress", updateLoadProgress);
          audio.addEventListener("loadedmetadata", updateLoadProgress);
          audio.addEventListener("canplay", updateLoadProgress);
        }
        const targetTime = video.currentTime;
        video.defaultMuted = true;
        video.muted = true;
        if (audio.readyState >= 1) {
          audio.currentTime = targetTime;
        } else {
          audio.addEventListener(
            "loadedmetadata",
            () => {
              audio!.currentTime = targetTime;
            },
            { once: true },
          );
        }
        await audio.play();
        await video.play();
        setIsPlaying(true);

        if ("mediaSession" in navigator) {
          navigator.mediaSession.metadata = new MediaMetadata({
            title: item.description || "TikTok video",
            artist: user?.displayName || user?.email || "davincicode.design",
            artwork: item.media[1]
              ? [{ src: item.media[1], sizes: "512x512" }]
              : [],
          });
          navigator.mediaSession.playbackState = "playing";
          navigator.mediaSession.setActionHandler("play", () => {
            audio?.play().catch(() => {});
          });
          navigator.mediaSession.setActionHandler(
            "pause",
            () => audio?.pause(),
          );
          navigator.mediaSession.setActionHandler("seekto", (details) => {
            if (typeof details.seekTime === "number" && audio) {
              audio.currentTime = details.seekTime;
              video.currentTime = details.seekTime;
            }
          });
        }
      } catch (error) {
        console.warn("Failed to enable web background playback:", error);
      }
    }, [backgroundMediaSource, isWeb, item.description, item.media, user]);

    const selectVideoVariant = useCallback(
      (variant: MediaVariant) => {
        const video = webVideoRef.current;
        pendingVideoTimeRef.current = video?.currentTime ?? null;
        rememberVideoVariantPreference(playbackPreferenceOwnerKey, variant);
        setSelectedVideoVariantId(variant.id);
      },
      [playbackPreferenceOwnerKey],
    );

    const selectAudioVariant = useCallback(
      (variant: MediaVariant | null) => {
        rememberAudioVariantPreference(playbackPreferenceOwnerKey, variant);
        setSelectedAudioVariantId(variant?.id || null);
      },
      [playbackPreferenceOwnerKey],
    );

    useEffect(() => {
      if (!isWeb || backgroundPlaybackEnabled) {
        return;
      }

      const audio = backgroundAudioRef.current;
      if (!audio) {
        return;
      }

      const video = webVideoRef.current;
      if (video && isActive) {
        video.currentTime = audio.currentTime;
        video.defaultMuted = false;
        video.muted = false;
        video.play().catch(() => {});
      }
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      audio.remove();
      backgroundAudioRef.current = null;
      setBackgroundLoadProgress(null);
    }, [backgroundPlaybackEnabled, isActive, isWeb]);

    useEffect(() => {
      if (!isWeb || backgroundPlaybackEnabled || !isActive) return;

      const existingAudio = backgroundAudioRef.current;
      if (existingAudio) {
        existingAudio.pause();
        existingAudio.removeAttribute("src");
        existingAudio.load();
        existingAudio.remove();
        backgroundAudioRef.current = null;
      }

      const video = webVideoRef.current;
      if (!selectedAudioVariant || !video) {
        if (video) {
          video.defaultMuted = !webAudioUnlocked;
          video.muted = !webAudioUnlocked;
        }
        return;
      }

      const audio = document.createElement("audio");
      audio.preload = "auto";
      audio.src = selectedAudioVariant.url;
      audio.style.display = "none";
      audio.currentTime = video.currentTime || 0;
      document.body.appendChild(audio);
      backgroundAudioRef.current = audio;
      video.defaultMuted = true;
      video.muted = true;
      if (!video.paused) {
        audio.play().catch((error) => {
          console.warn("Selected audio variant could not play:", error);
          setSelectedAudioVariantId(null);
          audio.remove();
          backgroundAudioRef.current = null;
          video.defaultMuted = !webAudioUnlocked;
          video.muted = !webAudioUnlocked;
        });
      }
    }, [
      backgroundPlaybackEnabled,
      isActive,
      isWeb,
      item.id,
      selectedAudioVariant?.id,
      selectedAudioVariant?.url,
      webAudioUnlocked,
    ]);

    useEffect(() => {
      if (!isWeb) {
        return;
      }

      const keepBackgroundAudioPlaying = () => {
        if (document.hidden && backgroundPlaybackEnabled && isActive) {
          backgroundAudioRef.current?.play().catch(() => {});
        }
      };
      document.addEventListener("visibilitychange", keepBackgroundAudioPlaying);
      return () => {
        document.removeEventListener(
          "visibilitychange",
          keepBackgroundAudioPlaying,
        );
      };
    }, [backgroundPlaybackEnabled, isActive, isWeb]);

    const seekToProgress = async (progress: number) => {
      if (isWeb) {
        const video = webVideoRef.current;
        if (!video || playbackProgress.durationMillis <= 0) {
          return;
        }
        const nextPosition = Math.round(
          Math.min(1, Math.max(0, progress)) * playbackProgress.durationMillis,
        );
        video.currentTime = nextPosition / 1000;
        if (backgroundAudioRef.current) {
          backgroundAudioRef.current.currentTime = nextPosition / 1000;
        }
        setPlaybackProgress((current) => ({
          ...current,
          positionMillis: nextPosition,
        }));
        showProgressTemporarily();
        return;
      }

      if (ref.current == null || playbackProgress.durationMillis <= 0) {
        return;
      }

      const nextPosition = Math.round(
        Math.min(1, Math.max(0, progress)) * playbackProgress.durationMillis,
      );
      setPlaybackProgress((current) => ({
        ...current,
        positionMillis: nextPosition,
      }));
      showProgressTemporarily();

      try {
        await ref.current.setPositionAsync(nextPosition);
      } catch (e) {
        console.log("Failed to seek:", e);
      }
    };

    const handlePlaybackStatusUpdate = useCallback(
      (status: AVPlaybackStatus) => {
        if (!status.isLoaded) {
          if (isActive) {
            playbackReadyRef.current = false;
            setMediaLoadProgress(18);
            setMediaLoadMessage("正在连接并缓冲视频…");
            if (!loadingDisplayTimerRef.current) {
              loadingDisplayTimerRef.current = setTimeout(() => {
                loadingDisplayTimerRef.current = null;
                if (!playbackReadyRef.current) setMediaLoading(true);
              }, 1000);
            }
          }
          return;
        }

        if (status.isBuffering && isActive) {
          playbackReadyRef.current = false;
          setMediaLoadProgress((current) =>
            Math.max(35, Math.min(92, current + 2)),
          );
          setMediaLoadMessage("网络较慢，正在缓冲视频…");
          if (!loadingDisplayTimerRef.current) {
            loadingDisplayTimerRef.current = setTimeout(() => {
              loadingDisplayTimerRef.current = null;
              if (!playbackReadyRef.current) setMediaLoading(true);
            }, 1000);
          }
          if (
            !isWeb &&
            !nativeBufferRecoveryTimerRef.current &&
            nativeBufferRecoveryCountRef.current < 2
          ) {
            const resumeAtMillis = status.positionMillis ?? 0;
            nativeBufferRecoveryTimerRef.current = setTimeout(() => {
              nativeBufferRecoveryTimerRef.current = null;
              nativeBufferRecoveryCountRef.current += 1;
              reportMediaEdgeFailure(videoSource.uri);
              selectMediaEdge(originalVideoUri)
                .then((edge) => {
                  if (edge.url === videoSource.uri) {
                    return ref.current?.playAsync();
                  }
                  console.log(
                    `[MEDIA_EDGE_RECOVERY] ${Platform.OS} ${edge.id} ${edge.latencyMs}ms`,
                  );
                  pendingVideoTimeRef.current = resumeAtMillis / 1000;
                  setBalancedVideoUri(edge.url);
                })
                .catch(() => ref.current?.playAsync());
            }, 8000);
          }
        } else if (isActive) {
          playbackReadyRef.current = true;
          if (loadingDisplayTimerRef.current) {
            clearTimeout(loadingDisplayTimerRef.current);
            loadingDisplayTimerRef.current = null;
          }
          if (nativeBufferRecoveryTimerRef.current) {
            clearTimeout(nativeBufferRecoveryTimerRef.current);
            nativeBufferRecoveryTimerRef.current = null;
          }
          setMediaLoadProgress(100);
          setMediaLoading(false);
        }

        if (
          !isWeb &&
          !status.isBuffering &&
          pendingVideoTimeRef.current !== null
        ) {
          const resumeAtMillis = Math.round(pendingVideoTimeRef.current * 1000);
          pendingVideoTimeRef.current = null;
          ref.current
            ?.setPositionAsync(resumeAtMillis)
            .then(() => ref.current?.playAsync())
            .catch(() => {});
        }

        setPlaybackProgress({
          positionMillis: status.positionMillis ?? 0,
          durationMillis: status.durationMillis ?? 0,
        });

        if (status.durationMillis) {
          const progress = (status.positionMillis || 0) / status.durationMillis;
          const milestone = Math.floor(progress * 10) / 10;
          if (
            milestone >= 0.1 &&
            (cacheCheckpointRef.current.url !== videoSource.uri ||
              milestone > cacheCheckpointRef.current.milestone)
          ) {
            cacheCheckpointRef.current = { url: videoSource.uri, milestone };
            void cacheHlsPlaybackCheckpoint(videoSource.uri, milestone);
          }
        }

        if (
          status.durationMillis &&
          (status.positionMillis || 0) >= status.durationMillis * 0.85 &&
          completedCacheRequestedRef.current !== videoSource.uri
        ) {
          completedCacheRequestedRef.current = videoSource.uri;
          void cacheCompletedHlsVideo(videoSource.uri);
        }

        if (!status.didJustFinish) {
          finishHandlingRef.current = false;
          if (isPlaying !== status.isPlaying) {
            setIsPlaying(status.isPlaying);
          }
          return;
        }

        if (finishHandlingRef.current) {
          return;
        }
        finishHandlingRef.current = true;

        const currentLoopMode = store.getState().playback.loopMode;
        if (currentLoopMode === "single") {
          ref.current
            ?.replayAsync()
            .then(() => {
              finishHandlingRef.current = false;
              setIsPlaying(true);
            })
            .catch((e) => {
              finishHandlingRef.current = false;
              console.log("Failed to replay:", e);
            });
          return;
        }

        onPlaybackFinish?.();
      },
      [
        isActive,
        isPlaying,
        isWeb,
        onPlaybackFinish,
        originalVideoUri,
        showProgressTemporarily,
        store,
        videoSource.uri,
      ],
    );

    const syncWebProgress = useCallback(() => {
      const video = webVideoRef.current;
      if (!video) {
        return;
      }
      const bufferedEnd = video.buffered.length
        ? video.buffered.end(video.buffered.length - 1)
        : 0;
      if (
        !video.paused &&
        video.readyState >= 3 &&
        bufferedEnd - video.currentTime >= 0.5
      ) {
        finishMediaLoading();
      }
      setPlaybackProgress({
        positionMillis: Math.round((video.currentTime || 0) * 1000),
        durationMillis: Math.round((video.duration || 0) * 1000),
      });
      if (video.duration > 0) {
        const progress = video.currentTime / video.duration;
        const milestone = Math.floor(progress * 10) / 10;
        if (
          milestone >= 0.1 &&
          (cacheCheckpointRef.current.url !== videoSource.uri ||
            milestone > cacheCheckpointRef.current.milestone)
        ) {
          cacheCheckpointRef.current = { url: videoSource.uri, milestone };
          void cacheHlsPlaybackCheckpoint(videoSource.uri, milestone);
        }
      }
      if (
        video.duration > 0 &&
        video.currentTime >= video.duration * 0.85 &&
        completedCacheRequestedRef.current !== videoSource.uri
      ) {
        completedCacheRequestedRef.current = videoSource.uri;
        void cacheCompletedHlsVideo(videoSource.uri);
      }
      if (Date.now() - lastResumeSaveRef.current >= 4000) {
        lastResumeSaveRef.current = Date.now();
        saveResumePosition(
          item.id,
          video.currentTime || 0,
          video.duration || 0,
        );
      }
      const externalAudio = backgroundAudioRef.current;
      if (
        externalAudio &&
        !externalAudio.paused &&
        Math.abs(externalAudio.currentTime - video.currentTime) > 0.45
      ) {
        externalAudio.currentTime = video.currentTime;
      }
    }, [finishMediaLoading, item.id, videoSource.uri]);

    const handleWebReady = useCallback(() => {
      const video = webVideoRef.current;
      applyPreferredWebAudio(video);
      if (video && pendingVideoTimeRef.current !== null) {
        video.currentTime = pendingVideoTimeRef.current;
        if (backgroundAudioRef.current) {
          backgroundAudioRef.current.currentTime = pendingVideoTimeRef.current;
        }
        pendingVideoTimeRef.current = null;
      } else if (video && resumeAppliedItemRef.current !== item.id) {
        const resumePosition = getResumePosition(item.id);
        if (
          resumePosition > 0 &&
          Number.isFinite(video.duration) &&
          resumePosition < video.duration - 3
        ) {
          video.currentTime = resumePosition;
        }
        resumeAppliedItemRef.current = item.id;
      }
      syncWebProgress();
      if (!isActive) {
        return;
      }
      setTimeout(() => {
        play();
      }, 0);
    }, [applyPreferredWebAudio, isActive, item.id, syncWebProgress]);

    const handleWebEnded = useCallback(() => {
      if (finishHandlingRef.current) {
        return;
      }
      finishHandlingRef.current = true;
      saveResumePosition(item.id, 0);
      completedCacheRequestedRef.current = videoSource.uri;
      void cacheCompletedHlsVideo(videoSource.uri);

      const video = webVideoRef.current;
      const currentLoopMode = store.getState().playback.loopMode;
      if (currentLoopMode === "single" && video) {
        video.currentTime = 0;
        video
          .play()
          .then(() => {
            finishHandlingRef.current = false;
            setIsPlaying(true);
          })
          .catch((e) => {
            finishHandlingRef.current = false;
            console.log("Failed to replay:", e);
          });
        return;
      }

      onPlaybackFinish?.();
    }, [item.id, onPlaybackFinish, showProgressTemporarily, store, videoSource.uri]);

    useEffect(() => {
      if (isActive) {
        play();
        return;
      }
      stop();
    }, [isActive]);

    useEffect(() => {
      if (
        !isWeb ||
        !isActive ||
        !webAudioUnlocked ||
        backgroundPlaybackEnabled
      ) {
        return;
      }
      const video = webVideoRef.current;
      if (!video) return;
      applyPreferredWebAudio(video);
      if (video.paused) {
        video.play().catch(() => {
          setWebAudioEnabled(false);
          video.defaultMuted = true;
          video.muted = true;
          video.play().catch(() => {});
        });
      }
    }, [
      applyPreferredWebAudio,
      backgroundPlaybackEnabled,
      isActive,
      isWeb,
      item.id,
      videoSource.uri,
      webAudioUnlocked,
    ]);

    const emitWebDragComplete = useCallback(
      (dx: number, dy: number) => {
        const now = Date.now();
        if (now - lastWebDragCompleteAtRef.current < 120) return;
        lastWebDragCompleteAtRef.current = now;
        onWebDragComplete?.(dx, dy);
      },
      [onWebDragComplete],
    );

    useEffect(() => {
      if (!isWeb) return;
      const element = webDragSurfaceRef.current as HTMLElement | null;
      if (!element) return;

      element.draggable = isActive;
      if (!isActive) return;

      const resetDrag = () => {
        webMouseDragRef.current = {
          active: false,
          startX: 0,
          startY: 0,
          lastX: 0,
          lastY: 0,
          moved: false,
        };
      };
      const handleMouseDown = (event: MouseEvent) => {
        if (event.button !== 0) return;
        webMouseDragRef.current = {
          active: true,
          startX: event.clientX,
          startY: event.clientY,
          lastX: event.clientX,
          lastY: event.clientY,
          moved: false,
        };
      };
      const handleMouseMove = (event: MouseEvent) => {
        const drag = webMouseDragRef.current;
        if (!drag.active) return;
        drag.lastX = event.clientX;
        drag.lastY = event.clientY;
        if (
          Math.max(
            Math.abs(drag.lastX - drag.startX),
            Math.abs(drag.lastY - drag.startY),
          ) >= 8
        ) {
          drag.moved = true;
        }
      };
      const handleMouseUp = (event: MouseEvent) => {
        const drag = webMouseDragRef.current;
        if (!drag.active) return;
        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;
        const moved = drag.moved;
        resetDrag();
        if (moved) {
          event.preventDefault();
          emitWebDragComplete(dx, dy);
        }
      };
      const handleDragStart = (event: DragEvent) => {
        const currentDrag = webMouseDragRef.current;
        webMouseDragRef.current = {
          active: true,
          startX: currentDrag.active ? currentDrag.startX : event.clientX,
          startY: currentDrag.active ? currentDrag.startY : event.clientY,
          lastX: event.clientX,
          lastY: event.clientY,
          moved: true,
        };
        event.dataTransfer?.setData("text/plain", "feed-drag");
        if (event.dataTransfer && typeof document !== "undefined") {
          const transparentDragImage = document.createElement("canvas");
          transparentDragImage.width = 1;
          transparentDragImage.height = 1;
          event.dataTransfer.setDragImage(transparentDragImage, 0, 0);
        }
      };
      const handleDrag = (event: DragEvent) => {
        const drag = webMouseDragRef.current;
        if (!drag.active || (event.clientX === 0 && event.clientY === 0))
          return;
        drag.lastX = event.clientX;
        drag.lastY = event.clientY;
      };
      const handleDragEnd = (event: DragEvent) => {
        const drag = webMouseDragRef.current;
        if (!drag.active) return;
        const useLastPosition = event.clientX === 0 && event.clientY === 0;
        const dx = (useLastPosition ? drag.lastX : event.clientX) - drag.startX;
        const dy = (useLastPosition ? drag.lastY : event.clientY) - drag.startY;
        resetDrag();
        emitWebDragComplete(dx, dy);
      };

      element.addEventListener("mousedown", handleMouseDown);
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      element.addEventListener("dragstart", handleDragStart);
      element.addEventListener("drag", handleDrag);
      element.addEventListener("dragend", handleDragEnd);
      return () => {
        element.draggable = false;
        element.removeEventListener("mousedown", handleMouseDown);
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        element.removeEventListener("dragstart", handleDragStart);
        element.removeEventListener("drag", handleDrag);
        element.removeEventListener("dragend", handleDragEnd);
      };
    }, [emitWebDragComplete, isActive, isWeb]);

    return (
      <Pressable
        ref={webDragSurfaceRef}
        style={styles.container}
        onPress={handleSurfacePress}
      >
        {isWeb ? (
          React.createElement("video", {
            ref: webVideoRef,
            src: isHlsSource ? undefined : videoSource.uri,
            poster: posterSource.uri,
            muted:
              backgroundPlaybackEnabled ||
              !webAudioUnlocked ||
              !webAudioEnabled,
            defaultMuted:
              backgroundPlaybackEnabled ||
              !webAudioUnlocked ||
              !webAudioEnabled,
            playsInline: true,
            "webkit-playsinline": "true",
            autoPlay: isActive,
            controls: isActive,
            loop: false,
            preload:
              effectivePreloadMode === "active"
                ? "auto"
                : effectivePreloadMode === "next"
                ? "metadata"
                : "none",
            disablePictureInPicture: false,
            draggable: false,
            style: styles.webVideo,
            onTimeUpdate: syncWebProgress,
            onLoadedMetadata: handleWebReady,
            onCanPlay: (event: { currentTarget: HTMLVideoElement }) => {
              handleWebReady();
              const video = event.currentTarget;
              const bufferedEnd = video.buffered.length
                ? video.buffered.end(video.buffered.length - 1)
                : 0;
              if (bufferedEnd - video.currentTime >= 1) finishMediaLoading();
            },
            onLoadStart: () => beginMediaLoading("正在连接视频服务器…"),
            onWaiting: () => beginMediaLoading("网速不足，正在缓冲下一段…"),
            onStalled: () => {
              beginMediaLoading("连接暂时停顿，正在等待服务器响应…");
              setMediaLoadProgress((current) => Math.max(72, current));
            },
            onProgress: (event: { currentTarget: HTMLVideoElement }) => {
              if (playbackReadyRef.current) return;
              const video = event.currentTarget;
              if (!video.buffered.length) return;
              const bufferedEnd = video.buffered.end(video.buffered.length - 1);
              const secondsAhead = Math.max(0, bufferedEnd - video.currentTime);
              const percent =
                62 + Math.min(32, Math.round((secondsAhead / 6) * 32));
              advanceMediaLoading(
                percent,
                `已缓冲 ${secondsAhead.toFixed(1)} 秒`,
              );
            },
            onEnded: handleWebEnded,
            onPlay: () => setIsPlaying(true),
            onPlaying: () => {
              applyPreferredWebAudio(webVideoRef.current);
              finishMediaLoading();
              setIsPlaying(true);
            },
            onPause: () => setIsPlaying(false),
            onError: () => {
              reportMediaEdgeFailure(videoSource.uri);
              if (videoSource.uri !== originalVideoUri) {
                setBalancedVideoUri(originalVideoUri);
              } else if (selectedVideoVariantId !== defaultMobileVariant?.id) {
                setSelectedVideoVariantId(defaultMobileVariant?.id || null);
              }
            },
          })
        ) : (
          <Video
            ref={ref}
            style={styles.media}
            resizeMode={ResizeMode.COVER}
            shouldPlay={false}
            isLooping={false}
            onPlaybackStatusUpdate={handlePlaybackStatusUpdate}
            usePoster
            posterSource={posterSource}
            posterStyle={{ resizeMode: "cover", height: "100%" }}
            source={videoSource}
          />
        )}
        {isActive ? (
          <ProductSpotlight
            placements={item.productPlacements}
            currentTimeSeconds={playbackProgress.positionMillis / 1000}
          />
        ) : null}
        {isActive && item.isAdvertisement ? (
          <View pointerEvents="none" style={styles.advertisementBadge}>
            <Ionicons name="radio-outline" size={15} color="#fff4dd" />
            <Text style={styles.advertisementBadgeText}>广告</Text>
          </View>
        ) : null}
        {isActive && progressVisible && (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="打开客户端下载二维码"
            hitSlop={8}
            onPress={() => setDownloadOpen(true)}
            style={({ pressed }) => [
              styles.playbackBrandBadge,
              pressed && styles.playbackBrandBadgePressed,
            ]}
          >
            <Image
              source={
                Platform.OS === "web"
                  ? { uri: `/tiktok/i18n/logos/${siteLocale}.png` }
                  : require("../../../../assets/haihuang-logo-gold.png")
              }
              style={styles.playbackBrandLogo}
              resizeMode="contain"
            />
          </Pressable>
        )}
        {isActive && progressVisible && (
          <View
            pointerEvents="none"
            onLayout={
              strategyHeaderEnabled
                ? (event) => {
                    const nextTop = Math.ceil(
                      event.nativeEvent.layout.y +
                        event.nativeEvent.layout.height +
                        8,
                    );
                    setStrategyProgressTop((currentTop) =>
                      currentTop === nextTop ? currentTop : nextTop,
                    );
                  }
                : undefined
            }
            style={[
              styles.playbackInfoBadge,
              strategyHeaderEnabled && styles.playbackInfoBadgeBelowStrategy,
            ]}
          >
            <View style={styles.playbackInfoRow}>
              <Ionicons name="layers-outline" size={13} color="#e2bd77" />
              <Text numberOfLines={1} style={styles.playbackInfoVersion}>
                {APP_VERSION_LABEL}
              </Text>
            </View>
            <View style={styles.playbackInfoRow}>
              <Ionicons name="server-outline" size={13} color="#e2bd77" />
              <Text numberOfLines={1} style={styles.playbackInfoSource}>
                {isHlsSource
                  ? playbackSourceLabel
                  : `播放来源：${networkSourceLabel(videoSource.uri)}`}
              </Text>
            </View>
            <View style={styles.playbackInfoRow}>
              <Ionicons name="location-outline" size={13} color="#e2bd77" />
              <Text numberOfLines={2} style={styles.playbackInfoNetwork}>
                {networkIdentityLabel}
              </Text>
            </View>
          </View>
        )}
        {isActive && mediaLoading && (
          <View pointerEvents="box-none" style={styles.mediaLoadingContainer}>
            <View style={styles.mediaLoadingCard}>
              <Text style={styles.mediaLoadingPercent}>
                {Math.round(mediaLoadProgress)}%
              </Text>
              <View style={styles.mediaLoadingTrack}>
                <View
                  style={[
                    styles.mediaLoadingFill,
                    { width: `${Math.max(4, mediaLoadProgress)}%` },
                  ]}
                />
              </View>
              <Text style={styles.mediaLoadingText}>{mediaLoadMessage}</Text>
              {mediaLoadProgress >= 86 && (
                <TouchableOpacity
                  accessibilityRole="button"
                  accessibilityLabel="重新连接视频"
                  activeOpacity={0.8}
                  onPress={retryMediaLoading}
                  style={styles.mediaRetryButton}
                >
                  <Ionicons color="white" name="refresh" size={16} />
                  <Text style={styles.mediaRetryText}>重新连接</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}
        {user && (
          <PostSingleOverlay
            user={user}
            post={item}
            isPlaying={isPlaying}
            playbackProgress={playbackProgress}
            progressVisible={progressVisible}
            onTogglePlayback={togglePlayback}
            onSeek={seekToProgress}
            strategyHeaderEnabled={strategyHeaderEnabled}
            progressTop={
              strategyHeaderEnabled ? strategyProgressTop : undefined
            }
            onFavoriteChange={onFavoriteChange}
            onEnableBackgroundPlayback={enableBackgroundPlayback}
            backgroundLoadProgress={backgroundLoadProgress}
            videoVariants={playbackVideoVariants}
            audioVariants={audioVariants}
            selectedVideoVariantId={selectedVideoVariant?.id || null}
            selectedAudioVariantId={selectedAudioVariant?.id || null}
            onSelectVideoVariant={selectVideoVariant}
            onSelectAudioVariant={selectAudioVariant}
          />
        )}
        {isWeb &&
          isActive &&
          (!webAudioUnlocked || !webAudioEnabled) &&
          !backgroundPlaybackEnabled && (
            <View pointerEvents="box-none" style={styles.audioUnlockContainer}>
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel="开启声音"
                activeOpacity={0.82}
                disabled={audioUnlockState === "loading"}
                onPress={enableWebAudio}
                style={[
                  styles.audioUnlockButton,
                  audioUnlockState === "failed" &&
                    styles.audioUnlockButtonFailed,
                ]}
              >
                <Ionicons
                  color="white"
                  name={
                    audioUnlockState === "failed"
                      ? "alert-circle-outline"
                      : "volume-high"
                  }
                  size={22}
                />
                <Text style={styles.audioUnlockText}>
                  {audioUnlockState === "loading"
                    ? "正在开启…"
                    : audioUnlockState === "failed"
                    ? "开启失败，点击重试"
                    : "开启声音"}
                </Text>
              </TouchableOpacity>
            </View>
          )}
        <Modal
          animationType="fade"
          onRequestClose={() => setDownloadOpen(false)}
          transparent
          visible={downloadOpen}
        >
          <View style={styles.downloadModalBackdrop}>
            <Pressable
              accessibilityLabel="关闭下载窗口"
              onPress={() => setDownloadOpen(false)}
              style={styles.downloadModalCloseArea}
            />
            <View style={styles.downloadModalCard}>
              <Text style={styles.downloadModalTitle}>客户端下载</Text>
              <Text style={styles.downloadModalSubtitle}>
                扫描二维码，或选择对应的安装包
              </Text>
              <Image
                resizeMode="contain"
                source={require("../../../../assets/download-center-qr.png")}
                style={styles.downloadModalQr}
              />
              <View style={styles.downloadModalActions}>
                <Pressable
                  accessibilityRole="link"
                  onPress={() => void Linking.openURL(APK_DOWNLOAD_URL)}
                  style={[
                    styles.downloadModalButton,
                    styles.downloadModalButtonAndroid,
                  ]}
                >
                  <Ionicons color="#17120b" name="logo-android" size={20} />
                  <Text style={styles.downloadModalButtonPrimaryText}>
                    下载 APK
                  </Text>
                </Pressable>
                <Pressable
                  accessibilityRole="link"
                  onPress={() => void Linking.openURL(IPA_DOWNLOAD_URL)}
                  style={[
                    styles.downloadModalButton,
                    styles.downloadModalButtonIos,
                  ]}
                >
                  <Ionicons color="#f5efe5" name="logo-apple" size={20} />
                  <Text style={styles.downloadModalButtonSecondaryText}>
                    下载 IPA
                  </Text>
                </Pressable>
              </View>
              <Pressable
                onPress={() => setDownloadOpen(false)}
                style={styles.downloadModalCloseButton}
              >
                <Text style={styles.downloadModalCloseText}>关闭</Text>
              </Pressable>
            </View>
          </View>
        </Modal>
      </Pressable>
    );
  },
);

export default PostSingle;
